// ============================================================================
// BLING ADAPTER — Personal Finance V2 (Blueprint §6.2 / Fase 5)
// Normaliza dados do snapshot Bling para formato comparável com Firestore.
// Resolve contatos, formas de pagamento e categorias DINAMICAMENTE do snapshot.
// Pure JS — sem dependência de React/Firebase.
// ============================================================================

// ---- INDEX BUILDERS (dynamic from snapshot) ----

/** Constrói mapa id→nome de contatos a partir do snapshot */
function buildContatoIndex(snapshot) {
    const idx = new Map();
    for (const c of (snapshot?.contatos?.items || [])) {
        idx.set(c.id, c.nome || c.fantasia || `ID:${c.id}`);
    }
    return idx;
}

/** Constrói mapa id→descricao de formas de pagamento */
function buildFormaPgtoIndex(snapshot) {
    const idx = new Map();
    for (const f of (snapshot?.formas_pagamentos?.items || [])) {
        idx.set(f.id, f.descricao || f.nome || `ID:${f.id}`);
    }
    return idx;
}

/** Constrói mapa id→{descricao, tipo} de categorias receitas/despesas Bling */
function buildCategoriaIndex(snapshot) {
    const idx = new Map();
    for (const c of (snapshot?.categorias_receitas_despesas?.items || [])) {
        idx.set(c.id, { descricao: c.descricao || `ID:${c.id}`, tipo: c.tipo || null });
    }
    return idx;
}

// ---- NORMALIZERS ----

function normalizeConta(item, type, contatoIdx, formaIdx) {
    const contatoName = item.contato?.nome
        || contatoIdx.get(item.contato?.id)
        || `ID:${item.contato?.id}`;
    return {
        blingId: item.id,
        type,
        date: item.vencimento,
        dataEmissao: item.dataEmissao || null,
        value: Math.round(Number(item.valor) * 100) / 100,
        contactId: item.contato?.id || null,
        contactName: contatoName,
        contactDoc: item.contato?.numeroDocumento || null,
        formaPgto: formaIdx.get(item.formaPagamento?.id) || `ID:${item.formaPagamento?.id}`,
        formaPgtoId: item.formaPagamento?.id || null,
        contaContabil: item.contaContabil?.descricao || null,
        situacao: item.situacao,
        origem: item.origem ? {
            tipo: item.origem.tipoOrigem,
            numero: item.origem.numero,
            valor: item.origem.valor,
        } : null,
        linkBoleto: item.linkBoleto || null,
        source: 'bling',
    };
}

// ---- PUBLIC API ----

/**
 * Extrai todas as contas normalizadas do snapshot Bling.
 * Resolve nomes de contatos e formas de pagamento dinamicamente.
 * @param {object} snapshot — recta_bling_snapshot.json completo
 * @returns {{ pagar: object[], receber: object[], kpis: object|null, categorias: Map, meta: object }}
 */
export function extractBlingContas(snapshot) {
    if (!snapshot) return { pagar: [], receber: [], kpis: null, categorias: new Map(), meta: null };

    const contatoIdx = buildContatoIndex(snapshot);
    const formaIdx = buildFormaPgtoIndex(snapshot);
    const categorias = buildCategoriaIndex(snapshot);

    const pagar = (snapshot.contas_pagar?.items || []).map(i => normalizeConta(i, 'PAGAR', contatoIdx, formaIdx));
    const receber = (snapshot.contas_receber?.items || []).map(i => normalizeConta(i, 'RECEBER', contatoIdx, formaIdx));

    const kpis = snapshot.kpis || null;
    const meta = {
        generatedAt: snapshot.generated_at || snapshot.snapshot_generated_at,
        version: snapshot.version,
        counts: {
            contatos: contatoIdx.size,
            formasPgto: formaIdx.size,
            categorias: categorias.size,
            contasPagar: pagar.length,
            contasReceber: receber.length,
        },
    };

    return { pagar, receber, kpis, categorias, meta };
}

/**
 * Filtra contas Bling por mês (YYYY-MM).
 */
export function filterBlingByMonth(contas, yearMonth) {
    return contas.filter(c => c.date && c.date.startsWith(yearMonth));
}

/**
 * Extrai KPIs resumo do snapshot Bling.
 */
export function extractBlingKpis(snapshot) {
    return snapshot?.kpis || null;
}
